import { ApiClient } from './client';
import type { Player } from '@cradle7/shared';
import { hydrateFromServer } from '../progressStore';
import { setDailyStatus } from '../dailyStatusStore';
import { drainQueue } from '../submitQueue';
import { parseStartParam } from '../deepLink';
import { setActiveDailyRun } from '../dailyRunStore';
import { useRunStore } from '../store/runStore';
import { useHideoutStore } from '../hideoutStore';
import { useLoadoutStore } from '../loadoutStore';
import { useCatStore } from '../catStore';
import { useConsumablesStore } from '../consumablesStore';
import { hydrateChallenge } from '../challengeStore';
import { hydrateTournament } from '../tournamentStore';
import { setSeasonData } from '../seasonStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const api = new ApiClient(API_URL);

export async function bootAuth(): Promise<Player | null> {
  const tg = window.Telegram?.WebApp;

  if (tg) {
    try {
      tg.ready?.();
    } catch {}
  }

  const initData = tg?.initData;
  if (!initData) {
    return null;
  }

  try {
    let player: Player | null = null;
    if (api.token) {
      const me = await api.me().catch(() => null);
      if (me) player = me.player;
    }
    if (!player) {
      const auth = await api.authenticateTelegram(initData);
      player = auth.player;
    }

    // Hydrate progress from server. Failure is non-fatal — client falls back
    // to localStorage cache via progressStore.
    const [progress, daily, hideout, loadout, cats, consumables, season] = await Promise.all([
      api.meProgress().catch(() => null),
      api.meDaily().catch(() => null),
      api.meHideout().catch(() => null),
      api.meLoadout().catch(() => null),
      api.meCats().catch(() => null),
      api.meConsumables().catch(() => null),
      api.getSeason().catch(() => null),
      // Phase 2i — warm the daily-challenge cache. hydrateChallenge swallows
      // its own errors so it is safe to fire-and-forget inside Promise.all.
      hydrateChallenge(),
      // Phase 2k — warm the tournament cache for the same reason.
      hydrateTournament(),
    ]);
    if (progress) hydrateFromServer(progress);
    if (daily) setDailyStatus(daily);
    if (hideout) useHideoutStore.getState().hydrateFromServer(hideout);
    if (loadout) useLoadoutStore.getState().hydrateFromServer(loadout);
    if (cats) useCatStore.getState().hydrateFromServer(cats);
    if (consumables) useConsumablesStore.getState().hydrateFromServer(consumables);
    // Phase 3l — season pass. Failure is non-fatal; SeasonStrip renders skeleton.
    if (season) setSeasonData(season);

    // Flush submissions queued during prior offline sessions.
    void drainQueue(async (sub) => {
      try {
        await api.submitRun(sub);
        return true;
      } catch {
        return false;
      }
    });

    try {
      const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
      const intent = parseStartParam(startParam);
      if (intent.kind === 'daily') {
        const dr = await api.dailyRun().catch(() => null);
        if (dr) {
          setActiveDailyRun(dr);
          useRunStore.getState().startRun(dr.seed);
        }
      } else if (intent.kind === 'challenge') {
        useRunStore.getState().startRun(intent.seed);
      }
    } catch (err) {
      console.warn('[bootAuth] deep-link dispatch failed:', err);
    }

    return player;
  } catch (err) {
    console.warn('[bootAuth] failed; continuing offline:', err);
    return null;
  }
}

let lastServerRunId: string | null = null;
const lastRunListeners = new Set<() => void>();

export function setLastServerRunId(id: string | null): void {
  lastServerRunId = id;
  lastRunListeners.forEach((fn) => fn());
}
export function getLastServerRunId(): string | null {
  return lastServerRunId;
}
export function subscribeLastServerRunId(fn: () => void): () => void {
  lastRunListeners.add(fn);
  return () => { lastRunListeners.delete(fn); };
}

let lastFishGained = 0;
const lastFishGainedListeners = new Set<() => void>();

export function setLastFishGained(n: number): void {
  lastFishGained = n;
  lastFishGainedListeners.forEach((fn) => fn());
}

export function getLastFishGained(): number {
  return lastFishGained;
}

export function subscribeLastFishGained(fn: () => void): () => void {
  lastFishGainedListeners.add(fn);
  return () => {
    lastFishGainedListeners.delete(fn);
  };
}
